import {
  useState,
  useEffect,
  Fragment,
  createContext,
  useContext,
} from "react";
import { useStore } from "../../contexts/StoreContext";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import {
  updateForm,
  updateCustomField,
  deleteCustomField,
} from "../../api/mutations";
import { fetchForm, fetchPasses } from "../../api/queries";
import { useNavigate, useParams } from "react-router-dom";
import { useLanguage } from "../../contexts/LanguageContext";
import FormPreview from "./components/FormPreview";
import FormBuilder from "./components/FormBuilder";
import { isHiddenFieldType } from "../../constants/fieldTypes";
import { formFieldFromStoreField } from "../../utils/formFields";
import Button from "../../components/Button";
import Header from "../../components/Header";
import { usePlanAlert } from "../../contexts/PlanAlertContext";
import React from "react";

// Create a localized language context for this page
const LocalLanguageContext = createContext();

function LocalLanguageProvider({ children, targetLanguage }) {
  const [translations, setTranslations] = useState({});

  useEffect(() => {
    const loadTranslations = async (lang) => {
      try {
        const response = await import(`../../locales/${lang}.json`);
        setTranslations(response.default);
      } catch (error) {
        console.error(`Failed to load translations for ${lang}:`, error);
        // Fallback to English if translation fails
        if (lang !== "en") {
          const enResponse = await import("../../locales/en.json");
          setTranslations(enResponse.default);
        }
      }
    };

    if (targetLanguage) {
      loadTranslations(targetLanguage);
    }
  }, [targetLanguage]);

  const t = (key, variables) => {
    const keys = key.split(".");
    let value = translations;
    for (const k of keys) {
      value = value?.[k];
    }

    if (!value) return key;
    if (!variables) return value;

    // Handle plural forms first if they exist
    const pluralRegex =
      /\{(\w+),\s*plural,\s*one\s*\{([^}]*)\}\s*other\s*\{([^}]*)\}\}/g;
    let processedValue = value;

    processedValue = processedValue.replace(
      pluralRegex,
      (match, varName, oneForm, otherForm) => {
        const count = variables[varName];
        if (count === undefined) return match;
        return count === 1 ? oneForm : otherForm;
      },
    );

    // Split the string into parts based on variable placeholders
    const parts = processedValue.split(/(\{[^}]+\})/g);
    let hasJSX = false;

    const result = parts.map((part, index) => {
      const match = part.match(/\{(\w+)\}/);
      if (!match) return part;

      const varName = match[1];
      const replacement = variables[varName];

      if (replacement === undefined || replacement === null) {
        return match[0];
      }

      if (React.isValidElement(replacement)) {
        hasJSX = true;
        return React.cloneElement(replacement, { key: index });
      }

      return String(replacement);
    });

    return hasJSX ? result : result.join("");
  };

  return (
    <LocalLanguageContext.Provider value={{ language: targetLanguage, t }}>
      {children}
    </LocalLanguageContext.Provider>
  );
}

const useLocalLanguage = () => useContext(LocalLanguageContext);

export default function EditForm({
  id: propId,
  setupMode,
  setEditingId,
  setEditingIndex,
}) {
  const { t, language } = useLanguage();
  const { id: urlId } = useParams();
  const id = propId || urlId;
  const navigate = useNavigate();
  const { selectedStore } = useStore();
  const queryClient = useQueryClient();
  const [formType, setFormType] = useState(0);
  const [fields, setFields] = useState([]);
  const [description, setDescription] = useState("");
  const [submitText, setSubmitText] = useState("");
  const [selectedPass, setSelectedPass] = useState(null);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    description: "",
    submitText: "Submit",
    backgroundColor: "#FFFFFF",
    textColor: "#000000",
  });
  const [isSaving, setIsSaving] = useState(false);
  const [editingFieldId, setEditingFieldId] = useState(null);
  const [originalFieldState, setOriginalFieldState] = useState(null);
  const [showOptionsMap, setShowOptionsMap] = useState({});
  const [optionsState, setOptionsState] = useState({});
  const [isMarketingFieldEditing, setIsMarketingFieldEditing] = useState(false);
  const [originalMarketingField, setOriginalMarketingField] = useState(null);
  const [formLanguage, setFormLanguage] = useState("en");
  const { showPlanAlert } = usePlanAlert();

  // Fetch document data
  const { data: document, isLoading } = useQuery({
    queryKey: ["document", selectedStore?.id, id],
    queryFn: () => {
      if (!selectedStore?.id || !id) {
        throw new Error("Store ID or Form ID is missing");
      }
      return fetchForm(selectedStore.id, id);
    },
    enabled: !!selectedStore?.id && !!id,
  });

  // Fetch passes for the store
  const { data: passes, isLoading: passesLoading } = useQuery({
    queryKey: ["passes", selectedStore?.id],
    queryFn: () => fetchPasses(selectedStore?.id),
    enabled: !!selectedStore?.id,
  });

  // Set form language when document is loaded
  useEffect(() => {
    if (document && document.lang) {
      setFormLanguage(document.lang);
    }
  }, [document]);

  // Populate form data when document is loaded
  useEffect(() => {
    console.log("Store data effect:", {
      store: document,
      selectedStoreId: selectedStore?.id,
      isLoading,
      error,
    });
    if (document) {
      setFormType(document.kind);
      // Check if both email and phone are unique in the existing fields
      const fields = document.content.fields;
      const hasUniqueEmail = fields.some((f) => f.type === "email" && f.unique);
      const hasUniquePhone = fields.some((f) => f.type === "tel" && f.unique);

      // If both are unique, make email unique and phone non-unique
      if (hasUniqueEmail && hasUniquePhone) {
        setFields(
          fields.map((field) => {
            if (field.type === "email") {
              return { ...field, unique: true, required: true };
            }
            if (field.type === "tel") {
              return { ...field, unique: false };
            }
            return field;
          }),
        );
      } else {
        // Otherwise, keep the existing unique field
        setFields(
          fields.map((field) => {
            if (field.type === "email" && field.unique) {
              return { ...field, unique: true, required: true };
            }
            return field;
          }),
        );
      }

      setDescription(document.description);
      setSubmitText(document.submit_text);
      setFormData((prev) => ({
        ...prev,
        description: document.description,
        submitText: document.submit_text,
        backgroundColor: document.background_color || "#FFFFFF",
        textColor: document.text_color || "#000000",
      }));

      // Initialize showOptionsMap for all fields that should show options
      if (selectedStore?.custom_fields) {
        const initialOptionsMap = {};
        selectedStore.custom_fields.forEach((field) => {
          initialOptionsMap[field.id] = [
            "select",
            "radio",
            "checkbox",
          ].includes(field.field_type);
        });
        setShowOptionsMap(initialOptionsMap);
      }

      if (document.pass.id) {
        const pass = passes?.find((p) => p.id === document.pass.id);
        setSelectedPass(pass);
      }

      // Store the original marketing field when first loading
      const marketingField = fields.find((f) => f.field === "marketing");
      if (marketingField && !originalMarketingField) {
        console.log("Setting original marketing field:", marketingField);
        setOriginalMarketingField({ ...marketingField });
      }
    }
  }, [document, passes]);

  useEffect(() => {
    if (selectedStore?.custom_fields) {
      const marketingField = selectedStore.custom_fields.find(
        (f) => f.field === "marketing",
      );
      if (marketingField) {
        setOriginalMarketingField(marketingField);
      }
    }
  }, [selectedStore]);

  const updateFormMutation = useMutation({
    mutationFn: async (formData) => {
      if (!selectedStore?.id) {
        console.error("Store ID missing:", selectedStore);
        throw new Error("Store ID is missing");
      }
      if (!id) {
        console.error("Form ID missing:", id);
        throw new Error("Form ID is missing");
      }

      console.log("Updating form with:", formData);

      return updateForm({
        storeId: selectedStore.id,
        formId: id,
        kind: formData.kind,
        passId: formData.passId,
        description: formData.description,
        submitText: formData.submitText,
        content: formData.content,
        backgroundColor: formData.backgroundColor,
        textColor: formData.textColor,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["forms", selectedStore?.id] });
      // Only navigate if this is a regular form save, not from deleting a store custom field
      if (!variables.skipNavigation) {
        if (setupMode && setEditingId && setEditingIndex) {
          setEditingId(null);
          setEditingIndex(null);
        } else {
          navigate("/forms");
        }
      }
    },
    onError: (error) => {
      console.error("Error updating form:", error);
      setError(error.message);
    },
  });

  const updateCustomFieldMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      if (!selectedStore?.id) throw new Error("Store ID is missing");
      console.log("Mutation called with:", { id, data });
      return updateCustomField({
        storeId: selectedStore.id,
        fieldId: id,
        data: {
          // A visible field keys itself off its label, so renaming one renames
          // its key too. A hidden field's key is the query parameter it reads
          // from the signup link: re-deriving that from the label would point
          // it at a parameter the ad links never carry, and the field would
          // quietly stop collecting anything.
          field: isHiddenFieldType(data.field_type) ? data.field : data.label,
          label: data.label,
          field_type: data.field_type,
          options: data.options,
        },
      });
    },
    onSuccess: async (response, { id, data }) => {
      console.log("Update mutation success:", { response, id, data });

      // Find the old field in the store's custom fields
      const oldStoreField = selectedStore?.custom_fields?.find(
        (f) => f.id === id,
      );
      console.log("Found old store field:", oldStoreField);
      if (!oldStoreField) return;

      // Remove any duplicate fields with the same new label
      const filteredFields = fields.filter(
        (field) =>
          // Keep the field we're updating
          (field.type === oldStoreField.field_type &&
            field.label === oldStoreField.label) ||
          // Keep fields that don't match the new label
          !(field.type === data.field_type && field.label === data.label),
      );

      // Update the fields array to handle the label change
      const updatedFields = filteredFields.map((field) => {
        // Match the old field by type and old label
        if (
          field.type === oldStoreField.field_type &&
          field.label === oldStoreField.label
        ) {
          console.log("Updating field:", {
            from: {
              type: field.type,
              label: field.label,
              field: field.field,
            },
            to: {
              type: data.field_type,
              label: data.label,
              field: data.label,
            },
          });

          return {
            ...field,
            type: data.field_type,
            label: data.label,
            field: isHiddenFieldType(data.field_type) ? data.field : data.label,
            options: data.options || field.options,
          };
        }
        return field;
      });

      console.log("Setting fields with:", updatedFields);
      setFields(updatedFields);

      // Update optionsState if needed
      if (data.options) {
        setOptionsState((prev) => ({
          ...prev,
          [id]: data.options,
        }));
      }

      // Clear editing state
      setEditingFieldId(null);
      setOriginalFieldState(null);

      // Save the form to persist the changes
      try {
        await updateFormMutation.mutateAsync({
          storeId: selectedStore.id,
          formId: id,
          kind: formType,
          passId: selectedPass?.id,
          description,
          submitText,
          content: {
            fields: updatedFields,
          },
          backgroundColor: formData.backgroundColor,
          textColor: formData.textColor,
          skipNavigation: true,
        });
      } catch (error) {
        console.error("Error saving form after field update:", error);
      }

      // Invalidate the query cache
      queryClient.invalidateQueries({
        queryKey: ["document", selectedStore?.id, id],
      });
    },
    onError: (error) => {
      console.error("Update custom field error:", error);
      setError(error.message);
    },
  });

  const deleteCustomFieldMutation = useMutation({
    mutationFn: async (fieldId) => {
      if (!selectedStore?.id) throw new Error("Store ID is missing");
      return deleteCustomField({
        storeId: selectedStore.id,
        fieldId,
      });
    },
    onSuccess: async (_, fieldId) => {
      // Find the field that was deleted
      const deletedField = selectedStore?.custom_fields?.find(
        (f) => f.id === fieldId,
      );
      if (deletedField) {
        // Remove the field from the form fields if it exists
        setFields((prevFields) =>
          prevFields.filter(
            (f) =>
              !(
                f.type === deletedField.field_type &&
                f.label === deletedField.label
              ),
          ),
        );

        // Save the form to persist the changes, but skip navigation
        try {
          await updateFormMutation.mutateAsync({
            storeId: selectedStore.id,
            formId: id,
            kind: formType,
            passId: selectedPass?.id,
            description: description,
            submitText: submitText,
            content: {
              fields: fields.filter(
                (f) =>
                  !(
                    f.type === deletedField.field_type &&
                    f.label === deletedField.label
                  ),
              ),
            },
            backgroundColor: formData.backgroundColor,
            textColor: formData.textColor,
            skipNavigation: true, // Add this flag to prevent navigation
          });
        } catch (error) {
          console.error("Error saving form after field deletion:", error);
        }
      }
      queryClient.invalidateQueries({
        queryKey: ["document", selectedStore?.id, id],
      });
    },
    onError: (error) => {
      setError(error.message);
    },
  });

  const handleAddField = () => {
    if (!selectedStore?.features.is_custom_form_fields_enabled) {
      // t (the dashboard translator), not formT: the plan alert is dashboard
      // chrome, so it follows the merchant's language rather than the language
      // the form is being edited in.
      showPlanAlert({
        title: t("common.planAlertTitle"),
        description: t("common.planAlertDescription", {
          plan: "Growth",
        }),
      });
      return;
    }

    setFields([
      ...fields,
      {
        type: "text",
        label: "",
        field: "",
        required: false,
        placeholder: "",
      },
    ]);
  };

  const handleFieldChange = (index, field) => {
    if (index < 0) return;

    const newFields = [...fields];

    // If setting unique to true, also set required to true
    if (field.unique && !field.required) {
      field.required = true;
    }

    // If setting required to false and field is unique, prevent the change
    if (!field.required && newFields[index].unique) {
      return; // Don't allow unchecking required if unique is checked
    }

    newFields[index] = field;
    setFields(newFields);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedStore?.id) {
      setError("Store not selected");
      return;
    }
    if (!id) {
      setError("Form ID is missing");
      return;
    }
    setIsSaving(true);

    try {
      const hasUniqueEmailOrPhone = fields.some(
        (field) =>
          (field.type === "email" || field.type === "tel") &&
          field.unique &&
          field.required,
      );

      if (!hasUniqueEmailOrPhone) {
        setError(
          "Please mark at least one Email or Phone field as both unique and required",
        );
        return;
      }

      setError(null);
      const uniqueFields = fields.reduce((acc, field) => {
        const existingField = acc.find(
          (f) => f.type === field.type && f.label === field.label,
        );

        // Only add the field if it doesn't already exist
        if (!existingField) {
          acc.push(field);
        }
        return acc;
      }, []);

      await updateFormMutation.mutateAsync({
        storeId: selectedStore.id,
        formId: id,
        kind: formType,
        passId: selectedPass?.id,
        description: description,
        submitText: submitText,
        content: {
          fields: uniqueFields,
        },
        backgroundColor: formData.backgroundColor,
        textColor: formData.textColor,
      });
    } catch (error) {
      setError(error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const isFieldUsedInPassLayout = (storeField) => {
    if (!document) return false;

    if (!selectedPass) return false;
    return (
      selectedPass.pass.header_field === storeField.label ||
      selectedPass.pass.left_field === storeField.label ||
      selectedPass.pass.right_field === storeField.label
    );
  };

  const isFieldUsedInAnyPassLayout = (storeField) => {
    if (!passes) return false;
    return passes.some(
      (pass) =>
        pass.pass.header_field === storeField.label ||
        pass.pass.left_field === storeField.label ||
        pass.pass.right_field === storeField.label,
    );
  };

  const addFieldIfNotExists = (storeField, originalState) => {
    const fieldExists = fields.some(
      (f) => f.type === storeField.field_type && f.label === storeField.label,
    );

    if (!fieldExists) {
      setFields([...fields, formFieldFromStoreField(storeField, originalState)]);
    }
  };

  const handleFieldRequirementChange = (isEnabled, storeField, isRequired) => {
    console.log(isEnabled, storeField, isRequired);

    if (!isEnabled) {
      // If the field is not enabled, enable it first
      addFieldIfNotExists(storeField);
    }

    // Update the requirement status
    setFields((prevFields) => {
      return prevFields.map((field) => {
        if (
          (field.type === storeField.type ||
            field.type === storeField.field_type) &&
          field.label === storeField.label
        ) {
          return { ...field, required: isRequired };
        }
        return field;
      });
    });
  };

  if (isLoading) {
    return (
      <div className="bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">{t("forms.edit.loading")}</div>
      </div>
    );
  }

  return (
    <LocalLanguageProvider targetLanguage={formLanguage}>
      <EditFormContent
        formType={formType}
        description={description}
        submitText={submitText}
        fields={fields}
        formData={formData}
        selectedPass={selectedPass}
        isSaving={isSaving}
        error={error}
        selectedStore={selectedStore}
        passes={passes}
        editingFieldId={editingFieldId}
        originalFieldState={originalFieldState}
        showOptionsMap={showOptionsMap}
        optionsState={optionsState}
        isMarketingFieldEditing={isMarketingFieldEditing}
        onDescriptionChange={setDescription}
        onSubmitTextChange={setSubmitText}
        onFormDataChange={setFormData}
        onSelectedPassChange={setSelectedPass}
        onFieldsChange={setFields}
        handleFieldRequirementChange={handleFieldRequirementChange}
        handleAddField={handleAddField}
        handleFieldChange={handleFieldChange}
        handleSubmit={handleSubmit}
        setEditingFieldId={setEditingFieldId}
        setOriginalFieldState={setOriginalFieldState}
        setShowOptionsMap={setShowOptionsMap}
        setOptionsState={setOptionsState}
        setIsMarketingFieldEditing={setIsMarketingFieldEditing}
        updateCustomFieldMutation={updateCustomFieldMutation}
        deleteCustomFieldMutation={deleteCustomFieldMutation}
        isFieldUsedInPassLayout={isFieldUsedInPassLayout}
        isFieldUsedInAnyPassLayout={isFieldUsedInAnyPassLayout}
        addFieldIfNotExists={addFieldIfNotExists}
        passesLoading={passesLoading}
        navigate={navigate}
        language={language}
        formLanguage={formLanguage}
      />
    </LocalLanguageProvider>
  );
}

// Separate component that uses the localized language context
function EditFormContent({
  formType,
  description,
  submitText,
  fields,
  formData,
  selectedPass,
  isSaving,
  error,
  selectedStore,
  passes,
  editingFieldId,
  originalFieldState,
  showOptionsMap,
  optionsState,
  isMarketingFieldEditing,
  onDescriptionChange,
  onSubmitTextChange,
  onFormDataChange,
  onSelectedPassChange,
  onFieldsChange,
  handleFieldRequirementChange,
  handleAddField,
  handleFieldChange,
  handleSubmit,
  setEditingFieldId,
  setOriginalFieldState,
  setShowOptionsMap,
  setOptionsState,
  setIsMarketingFieldEditing,
  updateCustomFieldMutation,
  deleteCustomFieldMutation,
  isFieldUsedInPassLayout,
  isFieldUsedInAnyPassLayout,
  addFieldIfNotExists,
  passesLoading,
  navigate,
  language,
  formLanguage,
}) {
  const { t: formT } = useLocalLanguage();

  const PREDEFINED_FIELDS = {
    name: {
      type: "text",
      label: formT("forms.new.initial.name"),
      unique: false,
      required: true,
      placeholder: formT("forms.new.initial.placeholder.name"),
      field: "name",
    },
    email: {
      type: "email",
      label: formT("forms.new.initial.email"),
      unique: true,
      required: true,
      placeholder: formT("forms.new.initial.placeholder.email"),
      field: "email",
    },
    phone: {
      type: "tel",
      label: formT("forms.new.initial.phone"),
      unique: true,
      required: false,
      placeholder: formT("forms.new.initial.placeholder.phone"),
      field: "phone",
    },
    birthday: {
      type: "date",
      label: formT("forms.new.initial.birthday"),
      unique: false,
      required: false,
      placeholder: formT("forms.new.initial.placeholder.birthday"),
      field: "birthday",
    },
    marketing: {
      type: "checkbox",
      label: formT("forms.new.initial.marketing"),
      unique: false,
      required: false,
      field: "marketing",
      options: [formT("forms.new.initial.marketing")],
    },
  };

  return (
    <div className="max-w-8xl mx-auto space-y-6 lg:space-y-8">
      <Header
        title={formT("forms.edit.title")}
        description={formT("forms.edit.description")}
        rightSection={
          <Button onClick={handleSubmit} isSaving={isSaving}>
            {formT("forms.edit.button")}
          </Button>
        }
      />
      <div className="grid lg:grid-cols-2 gap-6 lg:gap-8">
        {/* Form Builder Column */}
        <FormBuilder
          formType={formType}
          description={description}
          submitText={submitText}
          fields={fields}
          formData={formData}
          selectedPass={selectedPass}
          isSaving={isSaving}
          error={error}
          selectedStore={selectedStore}
          passes={passes}
          editingFieldId={editingFieldId}
          originalFieldState={originalFieldState}
          showOptionsMap={showOptionsMap}
          optionsState={optionsState}
          isMarketingFieldEditing={isMarketingFieldEditing}
          PREDEFINED_FIELDS={PREDEFINED_FIELDS}
          onDescriptionChange={onDescriptionChange}
          onSubmitTextChange={onSubmitTextChange}
          onFormDataChange={onFormDataChange}
          onSelectedPassChange={onSelectedPassChange}
          onFieldsChange={onFieldsChange}
          handleFieldRequirementChange={handleFieldRequirementChange}
          handleAddField={handleAddField}
          handleFieldChange={handleFieldChange}
          handleSubmit={handleSubmit}
          setEditingFieldId={setEditingFieldId}
          setOriginalFieldState={setOriginalFieldState}
          setShowOptionsMap={setShowOptionsMap}
          setOptionsState={setOptionsState}
          setIsMarketingFieldEditing={setIsMarketingFieldEditing}
          updateCustomFieldMutation={updateCustomFieldMutation}
          deleteCustomFieldMutation={deleteCustomFieldMutation}
          isFieldUsedInPassLayout={isFieldUsedInPassLayout}
          isFieldUsedInAnyPassLayout={isFieldUsedInAnyPassLayout}
          addFieldIfNotExists={addFieldIfNotExists}
          passesLoading={passesLoading}
          editMode={true}
          formT={formT} // the form's own language, not the dashboard's
        />

        {/* Preview Column */}
        {/* translate="no": faithful preview of the customer-facing form;
            machine-translating it would misrepresent what the customer
            actually sees, and keeps Translate out of the description's
            dangerouslySetInnerHTML subtree (see domPatches.js). */}
        <div translate="no">
          <div className="sticky top-20">
            <FormPreview
              selectedStore={selectedStore}
              formData={formData}
              description={description}
              fields={fields}
              submitText={submitText}
              handleFieldChange={handleFieldChange}
              formT={formT} // the form's own language, not the dashboard's
            />
          </div>
        </div>
      </div>
    </div>
  );
}
